import { Plus, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../../../components/modal/Modal";
import images from "../../../../assets/images";
import { AgGridReact } from "ag-grid-react";
import { useChecklistCategories, ChecklistCategoryFormData } from "./useChecklistCategories";

const emptyForm: ChecklistCategoryFormData = { categoryId: "", categoryName: "" };

const HazMatChecklistCategory: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<ChecklistCategoryFormData>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);

  const {
    categories,
    loading,
    error,
    createCategory,
    updateCategory,
    deleteCategory,
    clearError
  } = useChecklistCategories({
    autoFetch: true,
    refreshOnFocus: false
  });

  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [error, clearError]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEdit = (data: any) => {
    setFormData({
      categoryId: data.categoryId,
      categoryName: data.categoryName,
      _id: data._id,
    });
    setIsOpen(true);
    setEditId(data._id);
  };

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this checklist category?"
    );
    if (confirmed) {
      try {
        await deleteCategory(id);
      } catch (error) {
        console.error("Delete error:", error);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.categoryId.trim() || !formData.categoryName.trim()) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      if (editId) {
        await updateCategory(editId, {
          categoryId: formData.categoryId.trim(),
          categoryName: formData.categoryName.trim(),
        });
      } else {
        await createCategory({
          categoryId: formData.categoryId.trim(),
          categoryName: formData.categoryName.trim(),
        });
      }

      setFormData(emptyForm);
      setEditId(null);
      setIsOpen(false);
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const [columnDefs] = useState<any>([
    {
      field: "categoryId",
      headerName: "Category ID",
      minWidth: 30,
      maxWidth: 150,
      flex: 0,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: { textAlign: "left", padding: "10px 0 0 15px" },
    },
    {
      field: "categoryName",
      headerName: "Category Name",
      minWidth: 20,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 5px 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "categoryName",
    },
    {
      field: "createdAt",
      headerName: "Created At",
      minWidth: 20,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 5px 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      valueGetter: (params: any) => {
        return params.data?.createdAt
          ? new Date(params.data.createdAt).toLocaleDateString("en-US")
          : "NA";
      },
    },
    {
      field: "action",
      headerName: "Action",
      cellStyle: { padding: "10px 15px 0 0 ", textAlign: "left" },
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 0,
      minWidth: 40,
      maxWidth: 90,
      cellRenderer: (params: any) => (
        <div className="flex gap-x-3 pt-2">
          <button
            onClick={() => handleEdit(params.data)}
            className="p-1 hover:bg-gray-100 rounded"
            title="Edit Checklist Category"
          >
            <img className="iconTd w-5 h-5" src={images.editIcon} alt="edit" />
          </button>
          <button
            onClick={() => handleDelete(params.data._id)}
            className="p-1 hover:bg-red-100 rounded"
            title="Delete Checklist Category"
          >
            <Trash2 className="text-red-500" size={23} />
          </button>
        </div>
      ),
      filter: false,
    },
  ]);

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
      },
    };
  }, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Hazardous Material Checklist Categories</h2>
          <button
            onClick={() => {
              setIsOpen(true);
              setFormData(emptyForm);
              setEditId(null);
            }}
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-3 py-2 rounded-full"
            disabled={loading}
          >
            <Plus size={20} /> Add Category
          </button>
        </div>

        {error && typeof error === 'string' && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
            <button
              onClick={clearError}
              className="ml-2 text-red-500 hover:text-red-700"
            >
              ✕
            </button>
          </div>
        )}

        <div className="tableBlock w-full">
          <div className="card">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <p className="text-gray-500 text-lg">Loading...</p>
              </div>
            ) : categories?.length > 0 ? (
              <div
                className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg"
                style={{
                  '--ag-background-color': '#fff',
                  '--ag-foreground-color': '#2a2a2a',
                  '--ag-header-foreground-color': '#2a2a2a',
                  '--ag-header-background-color': '#E9F5FF',
                  '--ag-odd-row-background-color': 'rgba(0, 0, 0, 0.03)',
                  '--ag-header-column-resize-handle-color': 'rgb(126, 46, 132)',
                  '--ag-font-size': '0.75rem',
                  '--ag-header-font-weight': 'bold',
                } as React.CSSProperties}
              >
                <AgGridReact
                  rowData={categories}
                  columnDefs={columnDefs}
                  headerHeight={65}
                  rowHeight={50}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  No Checklist Categories added yet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setFormData(emptyForm);
          setEditId(null);
        }}
        closeButton={false}
        modalClass={"bg-white w-full max-w-xl rounded-xl relative"}
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">
            {editId ? "Edit" : "Add"} Hazardous Material Checklist Category
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="pr-5 mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                placeholder="Enter Category ID"
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#375998]"
                required
              />
            </div>
            <div className="pr-5 mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="categoryName"
                value={formData.categoryName}
                onChange={handleChange}
                placeholder="Enter Category Name"
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#375998]"
                required
              />
            </div>
          </form>
        </div>
        <div className="flex justify-end gap-4 mt-3 p-2 bg-[#5A7192] w-full rounded-b-lg">
          <button
            onClick={() => {
              setIsOpen(false);
              setFormData(emptyForm);
              setEditId(null);
            }}
            className="bg-[#8497B0] font-semibold text-white px-4 py-2 rounded-full hover:opacity-90"
            disabled={loading}
          >
            CANCEL
          </button>
          <button
            onClick={handleSubmit}
            className="bg-[#00A8A8] font-semibold text-white px-4 py-2 rounded-full hover:opacity-90"
            disabled={loading}
          >
            {loading ? "SUBMITTING..." : "SUBMIT"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default HazMatChecklistCategory;

